import { useState } from 'react'
import { useSupabaseQuery, useSupabaseMutation } from '../../hooks/useSupabase'
import Modal from '../shared/Modal'
import AccountForm from './AccountForm'

export default function AccountsTab({ selectedOrganization, selectedAccount, onSelectAccount }) {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingAccount, setEditingAccount] = useState(null)
  const [deletingAccount, setDeletingAccount] = useState(null)

  const { data: accounts, loading, error, refetch } = useSupabaseQuery('WhatsAppAccounts', {
    eq: { IsDeleted: false, ...(selectedOrganization && { WhatsAppOrganizationId: selectedOrganization.WhatsAppOrganizationId }) },
    order: { column: 'Name', ascending: true }
  })

  const { data: organizations } = useSupabaseQuery('WhatsAppOrganizations', {
    eq: { IsDeleted: false },
    order: { column: 'Name', ascending: true }
  })

  const { data: providerConfigs } = useSupabaseQuery('WhatsAppProviderConfigs', {
    order: { column: 'Name', ascending: true }
  })

  const { insert, update, softDelete, loading: mutating } = useSupabaseMutation('WhatsAppAccounts')

  const handleCreate = async (formData) => {
    const { error } = await insert({ ...formData, IsDeleted: false })
    if (!error) {
      setShowCreateModal(false)
      refetch()
    }
  }

  const handleUpdate = async (formData) => {
    const { error } = await update(editingAccount.WhatsAppAccountId, 'WhatsAppAccountId', formData)
    if (!error) {
      setEditingAccount(null)
      refetch()
      if (selectedAccount?.WhatsAppAccountId === editingAccount.WhatsAppAccountId) {
        onSelectAccount({ ...selectedAccount, ...formData })
      }
    }
  }

  const handleDelete = async () => {
    const { error } = await softDelete(deletingAccount.WhatsAppAccountId, 'WhatsAppAccountId')
    if (!error) {
      setDeletingAccount(null)
      refetch()
      if (selectedAccount?.WhatsAppAccountId === deletingAccount.WhatsAppAccountId) {
        onSelectAccount(null)
      }
    }
  }

  const getOrgName = (orgId) => {
    return organizations?.find(o => o.WhatsAppOrganizationId === orgId)?.Name || 'Unknown'
  }

  const getConfigName = (configId) => {
    return providerConfigs?.find(c => c.WhatsAppProviderConfigId === configId)?.Name || null
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <svg className="mx-auto h-12 w-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">Error loading accounts</h3>
        <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error.message}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">WhatsApp Accounts</h2>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
            {selectedOrganization
              ? `Accounts for: ${selectedOrganization.Name}`
              : 'Manage WhatsApp Business accounts'
            }
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition flex items-center space-x-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          <span>New Account</span>
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        {accounts && accounts.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden sm:table-cell">
                    Organization
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden md:table-cell">
                    Provider Config
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {accounts.map((account) => {
                  const isSelected = selectedAccount?.WhatsAppAccountId === account.WhatsAppAccountId
                  const configName = getConfigName(account.WhatsAppProviderConfigId)

                  return (
                    <tr
                      key={account.WhatsAppAccountId}
                      onClick={() => onSelectAccount(account)}
                      className={`cursor-pointer transition-colors ${
                        isSelected
                          ? 'bg-primary-50 dark:bg-primary-900/20'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {isSelected && (
                            <svg className="w-5 h-5 text-primary-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          )}
                          <div>
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {account.Name}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 sm:hidden">
                              {getOrgName(account.WhatsAppOrganizationId)}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 hidden sm:table-cell">
                        {getOrgName(account.WhatsAppOrganizationId)}
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 hidden md:table-cell">
                        {configName ? (
                          <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 rounded text-xs">
                            {configName}
                          </span>
                        ) : (
                          <span className="text-gray-400 italic">No config</span>
                        )}
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200">
                          Active
                        </span>
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setEditingAccount(account)
                          }}
                          className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 mr-3"
                        >
                          Edit
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setDeletingAccount(account)
                          }}
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No accounts</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Get started by creating your first WhatsApp account.</p>
          </div>
        )}
      </div>

      {showCreateModal && (
        <Modal title="Create WhatsApp Account" onClose={() => setShowCreateModal(false)}>
          <AccountForm
            organizations={organizations}
            providerConfigs={providerConfigs}
            onSubmit={handleCreate}
            onCancel={() => setShowCreateModal(false)}
            loading={mutating}
          />
        </Modal>
      )}

      {editingAccount && (
        <Modal title="Edit WhatsApp Account" onClose={() => setEditingAccount(null)}>
          <AccountForm
            account={editingAccount}
            organizations={organizations}
            providerConfigs={providerConfigs}
            onSubmit={handleUpdate}
            onCancel={() => setEditingAccount(null)}
            loading={mutating}
          />
        </Modal>
      )}

      {deletingAccount && (
        <Modal title="Delete Account" onClose={() => setDeletingAccount(null)} size="sm">
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Are you sure you want to delete <strong>{deletingAccount.Name}</strong>? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeletingAccount(null)}
                disabled={mutating}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={mutating}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition disabled:opacity-50 flex items-center space-x-2"
              >
                {mutating && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                )}
                <span>Delete</span>
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
